import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import FetchInfoErrorGif from "../../assets/fetch-info-error.gif";
import HeartImagePaymentStep from "../../assets/heart-image-payment-step.png";
import PaymentPageLoadingGif from "../../assets/payment-page-loading.gif";
import SubscriptionArrowGif from "../../assets/subscription-arrow.gif";
import { useRazorpayPayment, useSubscriptionLink } from "./queries";
import { PlanName, ThankYouLocationState } from "./types";

interface PaymentStepModalProps {
    show: boolean;
    onHide: () => void;
    jotformId: string;
    userId: string;
    planId: string;
    planName: PlanName | null;
    setShowThankYou: (v: boolean) => void;
    setThankYouState: (v: ThankYouLocationState) => void;
    setSubscriptionLoading: (v: boolean) => void;
    fromMobile?: boolean;
}

function PaymentStepModal({
    show,
    onHide,
    jotformId,
    userId,
    planId,
    planName,
    setShowThankYou,
    setThankYouState,
    setSubscriptionLoading,
    fromMobile = false,
}: PaymentStepModalProps) {
    const [showLoading, setShowLoading] = useState(false);

    const { data, isLoading, error, refetch } = useSubscriptionLink(
        planId,
        jotformId,
        userId,
        show
    );

    const fetchedEmail = data?.user_details?.email || "";

    // Wrap displayRazorpay to show loading before thank you
    const { displayRazorpay } = useRazorpayPayment({
        subscriptionData: data,
        userDetails: data?.user_details,
        refetch,
        setShowThankYou: (...args) => {
            setShowLoading(false);
            setShowThankYou(...args);
        },
        setThankYouState: (...args) => {
            // Pass fromMobile to ThankYouLocationState (fix typo: fromMolbile -> fromMobile)
            if (args[0]) {
                setThankYouState({ ...args[0], fromMobile });
            } else {
                setThankYouState(args[0]);
            }
        },
        planName,
        setSubscriptionLoading,
    });

    // function maskEmail(email: string): string {
    //     const [local, domain] = email.split('@');
    //     if (!local || !domain) return email;
    //     if (local.length <= 6) {
    //         return `${local[0]}${"*".repeat(Math.max(0, local.length - 2))}${local.slice(-1)}@${domain}`;
    //     }
    //     return `${local.slice(0, 3)}${"*".repeat(local.length - 6)}${local.slice(-3)}@${domain}`;
    // }

    let modalContent = null;
    if (showLoading) {
        modalContent = (
            <div className="loading-page-center">
                <img
                    src={PaymentPageLoadingGif}
                    alt="Loading..."
                    className="payment-modal-loading-gif"
                />
            </div>
        );
    } else if (isLoading) {
        modalContent = (
            <div className="payment-modal-loading-outer">
                <div className="payment-modal-loading-center">
                    <img
                        src={PaymentPageLoadingGif}
                        alt="Loading..."
                        className="payment-modal-loading-gif"
                    />
                    <div className="payment-modal-loading-text">
                        Checking if the User Sync completed and fetching info...
                    </div>
                </div>
                <div className="payment-modal-warning">
                    Do Not Press Back Or Switch App
                </div>
            </div>
        );
    } else if (error) {
        modalContent = (
            <div className="payment-modal-error-container">
                <div className="payment-modal-error-gif-wrap">
                    <img
                        src={FetchInfoErrorGif}
                        alt="Error fetching info"
                        className="payment-modal-error-gif"
                    />
                </div>
                <h4 className="payment-modal-error-heading">
                    We’re having trouble syncing your data!
                </h4>
                <p className="payment-modal-error-subtext">
                    Our support team will contact you shortly. Alternatively,
                    you can reach out to us at <b>support@habitnu.in</b>
                </p>
            </div>
        );
    } else if (data && data.subscription_status === "active") {
        modalContent = (
            <div className="payment-modal-active-container">
                <div className="payment-modal-icon-wrap">
                    <div className="payment-modal-icon-bg">
                        <img
                            src={HeartImagePaymentStep}
                            alt="Heart"
                            className="payment-modal-heart-img"
                        />
                    </div>
                </div>
                <h4 className="payment-modal-title">Subscription Active</h4>
                <p className="payment-modal-desc">
                    Your subscription is already active.
                </p>
                <div className="payment-modal-email-box">
                    <p className="payment-modal-email-desc">
                        Your login details was sent to:
                    </p>
                    <p className="payment-modal-email">{fetchedEmail}</p>
                </div>
                {data.next_billing_at && (
                    <div
                        className="payment-modal-next-billing"
                        style={{ marginTop: "1rem", fontWeight: 500 }}
                    >
                        <strong>Next Billing Date:</strong>{" "}
                        {new Date(data.next_billing_at).toLocaleDateString(
                            "en-IN",
                            { year: "numeric", month: "long", day: "numeric" }
                        )}
                    </div>
                )}
                <Button
                    variant="primary"
                    size="lg"
                    className="start-subscription-btn mt-3"
                    onClick={() => {
                        window.location.href = "/login";
                    }}
                >
                    Go to Login{" "}
                    <img
                        src={SubscriptionArrowGif}
                        alt="Go to Login"
                        className="subscription-arrow-gif"
                    />
                </Button>
            </div>
        );
    } else {
        modalContent = (
            <>
                <div className="payment-modal-icon-wrap">
                    <div className="payment-modal-icon-bg">
                        <img
                            src={HeartImagePaymentStep}
                            alt="Heart"
                            className="payment-modal-heart-img"
                        />
                    </div>
                </div>
                <h4 className="payment-modal-title">
                    You&apos;re Just One Step Away!
                </h4>
                <p className="payment-modal-desc">
                    Start your journey towards a healthier life.
                </p>
                <div className="payment-modal-email-box">
                    <p className="payment-modal-email-desc">
                        Your login details will be emailed to:
                    </p>
                    <p className="payment-modal-email">{fetchedEmail}</p>
                </div>
                <div
                    role="button"
                    tabIndex={0}
                    className="payment-modal-btn"
                    onClick={() => {
                        onHide();
                        void displayRazorpay();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            onHide();
                            void displayRazorpay();
                        }
                    }}
                >
                    Proceed to Payment
                    <img
                        src={SubscriptionArrowGif}
                        alt="Go to Login"
                        className="subscription-arrow-gif"
                    />
                </div>
                <div
                    style={{
                        width: "inherit",
                        padding: "8px 24px",
                        textAlign: "center",
                    }}
                >
                    <p className="payment-modal-email-desc-small">
                        Cancel your recurring payment anytime in the app.
                    </p>
                    {planName && planName.toLowerCase() !== "thrive core" && (
                        <p className="payment-modal-email-desc-small">
                            Doctor fees go directly to the doctor, consultations
                            are discretionary.
                        </p>
                    )}
                </div>
            </>
        );
    }

    // Fix: Remove modal-open class and overflow style from body when modal closes
    React.useEffect(() => {
        if (!show) {
            document.body.classList.remove("modal-open");
            document.body.style.overflow = "";
        }
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            contentClassName="payment-modal-content"
            dialogClassName="custom-payment-modal-width"
        >
            <Modal.Body className="payment-modal-body">
                {modalContent}
            </Modal.Body>
        </Modal>
    );
}

export default PaymentStepModal;
